<template>
  <div class="evento-card" v-if="conference">
    <div class="card-container">
      <div class="card-media">
        <EventoImageCard
          class="evento-image-card"
          :image="conference.image"
          :alt-text="altText"
        />
      </div>
      <div class="card-body">
        <EventoContentCard
          class="evento-content-card"
          :primary-color="primaryColor"
          :audience-target="audienceTarget"
          @subscribe-click="handleSubscribe"
          @more-info-click="openModal"
          v-bind="contentProps"
        />
      </div>
    </div>
    <ConferenceModal
      v-if="isModalVisible"
      :is-visible="isModalVisible"
      :image="conference.image"
      :name="conference.name"
      :start_time="conference.start_time"
      :description="conference.information"
      :is-subscribed="isSubscribed"
      @close="closeModal"
      @subscribe-click="handleSubscribe"
    />
  </div>
</template>

<script setup>
import { defineProps, computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import EventoImageCard from "./EventoImageCard.vue";
import EventoContentCard from "./EventoContentCard.vue";
import ConferenceService from "@/services/api/conference";
import ConferenceModal from "./ConferenceModal.vue";

const props = defineProps({
  conference: {
    type: Object,
    required: true,
    validator: (conf) => ["name", "start_time"].every((key) => key in conf),
  },
  conferenceId: {
    type: [Number, String],
    required: true,
  },
  primaryColor: {
    type: String,
    default: "#021f5b",
  },
  secondaryColor: {
    type: String,
    default: "#9760C7",
  },
  audienceTarget:{
    type: String,
    default: "",
  },
  index: {
    type: Number,
    required: true,
  },
  registrationEndDate: String,
});

const store = useStore();

const isModalVisible = ref(false);
const isSubscribed = ref(props.conference.is_subscribed || false);

const concessionaireId = computed(
  () => store.getters.getSelectedConcessionaire?.id
);

const altText = computed(() => 
  props.conference?.name 
    ? `Imagen de la conferencia: ${props.conference.name}`
    : 'Imagen de la conferencia'
);

const contentProps = computed(() => ({
  name: props.conference.name,
  start_time: props.conference.start_time,
  end_time: props.conference.end_time,
  audience: props.conference.audience,
  isSubscribed: isSubscribed.value,
}));

const openModal = () => {
  if (!props.conference.information) {
    Swal.fire({
      icon: "info",
      title: "Información no disponible",
      text: "No hay más información disponible sobre la jornada seleccionada.",
      confirmButtonColor: "#021f5b",
    });
    return;
  }

  isModalVisible.value = true;
};

const closeModal = () => {
  isModalVisible.value = false;
};

const checkSubscriptionStatus = async () => {
  try {
    if (!concessionaireId.value) return;
    
    // Usar el valor de is_subscribed que viene del objeto conference
    isSubscribed.value = props.conference.is_subscribed || false;
  } catch (error) {
    console.error("Error al verificar suscripción:", error);
    isSubscribed.value = false;
  }
};

const handleSubscribe = async () => {
  try {
    if (!concessionaireId.value) {
      throw new Error("No se ha seleccionado un concesionario");
    }

    if (isSubscribed.value) {
      // Cancelar suscripción
      await ConferenceService.unsubscribe(
        props.conference.id,
        concessionaireId.value
      );
      
      isSubscribed.value = false;
      
      Swal.fire({
        icon: "success",
        title: "¡Suscripción cancelada!",
        text: "Has cancelado tu registro a la conferencia",
        confirmButtonColor: props.primaryColor,
      });
    } else {
      // Suscribirse
      await ConferenceService.subscribe(
        props.conference.id,
        concessionaireId.value
      );
      
      isSubscribed.value = true;

      Swal.fire({
        icon: "success",
        title: "¡Inscripción exitosa!",
        text: "Te has registrado correctamente a la conferencia",
        confirmButtonColor: props.primaryColor,
      });
    }
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Error en suscripción",
      text: error.message || "Ocurrió un error al intentar registrarse",
      confirmButtonColor: props.secondaryColor,
    });
  }
};

onMounted(() => {
  checkSubscriptionStatus();
});
</script>

<style scoped>
.evento-card {
  display: flex;
}

.card-container {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e6e6e6;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 450px;
}

.card-media { order: 0; }
.card-body { order: 1; padding: 16px; }

@media (max-width: 992px) {
  .card-container { 
    border-radius: 12px;
    max-width: 400px;
  }
}
</style>